/**
 * GiftInvitePage
 * ─────────────────────────────────────────────────────────────
 * Premium, teaser-style, share-ready launch invitation for CARTAL.
 * The visitor types the recipient's name (and optionally a short
 * personal note) and downloads a cinematic 1080×1350 (4:5) card as PNG,
 * or shares it directly on WhatsApp / X / Instagram / Telegram.
 *
 * Route: /gift  (aliases: /invite)
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import {
  Download, Share2, Sparkles, Copy, CheckCheck, Send, Gift,
  MessageCircle, ArrowRight, ExternalLink,
} from "lucide-react";
import { useI18n } from "../lib/i18n";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_carvo-market/artifacts/c5durw9k_B010DD54-BD83-49AB-A5A3-946EA832B9AF.png";

// The public launch URL — hard-coded because this is a *shareable* asset;
// the QR code and printed URL must always point at the real domain, even
// when the page is viewed from preview / staging.
const SHARE_URL_BASE = "https://cartal.sa";
// iter227 — language-specific share URL. The backend /api/share/{lang}
// endpoint returns an HTML page with matching Open Graph metadata
// (title, description, image, locale) so WhatsApp / iMessage / Twitter
// preview cards render in the same language the sharer is viewing.
// Real users following the link get a meta-refresh to the main site
// with the correct ?lang= query preserved.
const shareUrlFor = (l) =>
  `${SHARE_URL_BASE}/api/share/${l === "en" ? "en" : "ar"}`;

/**
 * Rotating list of premium Arabic + English taglines. One is picked at
 * mount so different downloads carry different lines — every card feels
 * a little unique without any user effort.
 */
const TAGLINES = [
  {
    ar_hero: "قِد أكثر. عِش أفضل.",
    en_hero: "Drive more. Live better.",
    ar_tease: "تجربة سيارات لم تختبرها من قبل — من البيع إلى الملكية.",
    en_tease: "An automotive experience unlike anything you've tried — from listing to ownership.",
  },
  {
    ar_hero: "كل مركبة. كل خدمة. تحت سقف واحد.",
    en_hero: "Every vehicle. Every service. One roof.",
    ar_tease: "لا تخيّل — بل جرّب. المنصّة التي تُعيد تعريف السوق السعودي.",
    en_tease: "Don't imagine — try it. The platform redefining Saudi mobility.",
  },
  {
    ar_hero: "منصّة السيارات الأولى في السعودية.",
    en_hero: "Saudi's first automotive super-app.",
    ar_tease: "بيع، اشترِ، أمّن، موّل، وانقل الملكية — بلمسة واحدة.",
    en_tease: "Sell, buy, insure, finance, and transfer — in a single tap.",
  },
  {
    ar_hero: "من البيع إلى التمويل — أذكى وأسرع.",
    en_hero: "From selling to financing — smarter and faster.",
    ar_tease: "المنصّة التي كنت تنتظرها. الوقت الآن.",
    en_tease: "The platform you've been waiting for. The moment is now.",
  },
];

export default function GiftInvitePage() {
  const { lang, setLang } = useI18n();
  const cardRef = useRef(null);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [copyOk, setCopyOk] = useState(false);
  // iter225 — deterministic daily rotation: every visitor on the same
  // calendar day (in Riyadh time) gets the SAME tagline; the tagline
  // changes automatically at local midnight without any server action.
  // Uses the ISO date "YYYY-MM-DD" as the seed so the choice is stable
  // for 24h and rotates deterministically through the pool.
  const tagline = useMemo(() => {
    const now = new Date();
    // Force Riyadh timezone so 00:00 rotation is consistent for KSA users.
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Riyadh",
      year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(now);
    const y = parts.find(p => p.type === "year").value;
    const m = parts.find(p => p.type === "month").value;
    const d = parts.find(p => p.type === "day").value;
    const seedStr = `${y}-${m}-${d}`;
    // Simple stable string→int hash (DJB2-lite) — sufficient for indexing.
    let h = 5381;
    for (let i = 0; i < seedStr.length; i++) h = ((h << 5) + h + seedStr.charCodeAt(i)) >>> 0;
    return TAGLINES[h % TAGLINES.length];
  }, []);

  // Give web-fonts / logo / QR one paint to fully render before enabling
  // downloads — html-to-image can otherwise capture a half-drawn frame.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 900);
    return () => clearTimeout(t);
  }, []);

  const shareMessage = () => {
    const to = name ? (lang === "ar" ? `عزيزي ${name}، ` : `Hey ${name}, `) : "";
    const noteLine = note ? `\n${note}` : "";
    const SHARE_URL = shareUrlFor(lang);
    const base =
      lang === "ar"
        ? `${to}دعوة خاصة إلى كارتال — منصّة السيارات الأولى في السعودية 🚗✨${noteLine}\n${SHARE_URL}`
        : `${to}A special invite to CARTAL — Saudi's first automotive super-app 🚗✨${noteLine}\n${SHARE_URL}`;
    return base;
  };

  const download = async () => {
    if (!cardRef.current) return;
    if (!ready) return toast(lang === "ar" ? "جاري التجهيز…" : "Preparing…");
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2.5,
        backgroundColor: "#050505",
      });
      const link = document.createElement("a");
      link.download = `cartal-invite-${(name || "friend").replace(/\s+/g, "_")}.png`;
      link.href = dataUrl;
      link.click();
      toast.success(lang === "ar" ? "تم تنزيل البطاقة ✨" : "Card downloaded ✨");
    } catch (e) {
      toast.error(lang === "ar" ? "فشل التنزيل — حاول مجدداً" : "Download failed");
      console.error(e);
    }
  };

  const nativeShare = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, { cacheBust: true, pixelRatio: 2 });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "cartal-invite.png", { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        // iter226 — iOS Safari + WhatsApp drops the caption when only
        // {files, text} is passed. Passing `url` alongside makes the
        // native share sheet forward the marketing text AND a clickable
        // https://cartal.sa link together with the image (matches the
        // previously-working WhatsApp preview the user shared).
        await navigator.share({ title: "CARTAL", text: shareMessage(), url: shareUrlFor(lang), files: [file] });
      } else if (navigator.share) {
        await navigator.share({ title: "CARTAL", text: shareMessage(), url: shareUrlFor(lang) });
      } else {
        await navigator.clipboard.writeText(shareMessage());
        toast.success(lang === "ar" ? "تم نسخ الرسالة" : "Message copied");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(lang === "ar" ? "لم تكتمل المشاركة" : "Share failed");
    }
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(shareMessage());
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 1800);
    } catch {
      toast.error(lang === "ar" ? "تعذّر النسخ" : "Copy failed");
    }
  };

  const encoded = encodeURIComponent(shareMessage());
  const whatsappHref = `https://wa.me/?text=${encoded}`;
  const twitterHref = `https://twitter.com/intent/tweet?text=${encoded}`;
  const telegramHref = `https://t.me/share/url?url=${encodeURIComponent(shareUrlFor(lang))}&text=${encoded}`;

  return (
    <div className="min-h-screen py-8 sm:py-12 px-4 sm:px-6" data-testid="gift-invite-page">
      <div className="mx-auto max-w-6xl">
        {/* iter110 — language toggle (bilingual card) */}
        <div className="flex justify-end mb-3">
          <button
            type="button"
            onClick={() => setLang(lang === "ar" ? "en" : "ar")}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold border shadow-sm hover:shadow transition-shadow"
            style={{
              borderColor: "#c9a55c",
              color: "#0a0a0a",
              background: "linear-gradient(135deg,#fff8e6 0%,#f5e8bf 100%)",
            }}
            data-testid="gift-lang-toggle"
            aria-label={lang === "ar" ? "Switch to English" : "التبديل إلى العربية"}
          >
            <span dir="ltr">🌐</span>
            <span>{lang === "ar" ? "English" : "العربية"}</span>
          </button>
        </div>
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10">
          <div className="overline mb-2 justify-center inline-flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-[#c9a55c]" />
            {lang === "ar" ? "بطاقة إطلاق كارتال" : "CARTAL launch card"}
          </div>
          <h1 className="font-display text-3xl sm:text-5xl tracking-tight font-medium text-[#0a0a0a]">
            {lang === "ar" ? "شارك كارتال بأناقة" : "Share CARTAL in style"}
          </h1>
          <p className="text-sm sm:text-base text-[#6b6b6b] mt-3 max-w-2xl mx-auto leading-relaxed">
            {lang === "ar"
              ? "اكتب الاسم في المربّع الذهبي، نزّل البطاقة، وشاركها — بطاقة راقية تحمل شعار كارتال ورمز QR يفتح المنصّة مباشرة."
              : "Type a name in the golden box, download the card, and share — a premium invite with the CARTAL logo and a QR that opens the platform instantly."}
          </p>
        </div>

        <div className="grid lg:grid-cols-[380px_1fr] gap-6 sm:gap-10 items-start">
          {/* ── Controls (left) ────────────────────────────────────────── */}
          <div className="space-y-4 order-2 lg:order-1">
            <div className="rounded-2xl bg-white border border-[#0a0a0a]/10 p-5 shadow-sm">
              <label className="text-xs font-semibold text-[#6b6b6b] uppercase tracking-wider">
                {lang === "ar" ? "إلى ↓" : "To ↓"}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={lang === "ar" ? "اكتب اسم المستلم…" : "Type recipient name…"}
                maxLength={40}
                className="mt-2 w-full px-4 py-3 rounded-xl border-2 border-[#c9a55c]/30 focus:border-[#c9a55c] focus:ring-2 focus:ring-[#c9a55c]/20 outline-none text-lg font-semibold text-[#0a0a0a] transition-all"
                data-testid="gift-name-input"
              />

              <label className="text-xs font-semibold text-[#6b6b6b] uppercase tracking-wider mt-4 block">
                {lang === "ar" ? "لمسة شخصية (اختياري)" : "Personal note (optional)"}
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={lang === "ar" ? "مثال: جرّبها وأخبرني رأيك 🚗" : "e.g. Try it and tell me what you think 🚗"}
                maxLength={90}
                rows={2}
                className="mt-2 w-full px-4 py-2.5 rounded-xl border border-[#0a0a0a]/15 focus:border-[#c9a55c] focus:ring-2 focus:ring-[#c9a55c]/20 outline-none text-sm text-[#0a0a0a] resize-none"
                data-testid="gift-note-input"
              />

              <div className="mt-5 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={download}
                  disabled={!ready}
                  className="col-span-2 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-bold shadow-md hover:scale-[1.02] transition-transform disabled:opacity-50"
                  style={{ background: "linear-gradient(135deg,#f5d478,#c9a55c,#8a6f2e)", color: "#0a0a0a" }}
                  data-testid="gift-download-btn"
                >
                  <Download className="w-4 h-4" />
                  {lang === "ar" ? "تنزيل البطاقة (PNG)" : "Download card (PNG)"}
                </button>
                <button
                  type="button"
                  onClick={nativeShare}
                  className="inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-xs font-bold text-[#0a0a0a] bg-[#faf6ea] hover:bg-[#f5efe0] border border-[#c9a55c]/40 transition-colors"
                  data-testid="gift-share-btn"
                >
                  <Share2 className="w-3.5 h-3.5" />
                  {lang === "ar" ? "مشاركة" : "Share"}
                </button>
                <button
                  type="button"
                  onClick={copyText}
                  className="inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-xs font-bold text-[#0a0a0a] bg-[#faf6ea] hover:bg-[#f5efe0] border border-[#c9a55c]/40 transition-colors"
                  data-testid="gift-copy-btn"
                >
                  {copyOk ? <CheckCheck className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copyOk
                    ? (lang === "ar" ? "تم النسخ" : "Copied")
                    : (lang === "ar" ? "نسخ الرسالة" : "Copy text")}
                </button>
              </div>
            </div>

            {/* Direct social share links */}
            <div className="rounded-2xl bg-white border border-[#0a0a0a]/10 p-5 shadow-sm">
              <div className="text-xs font-semibold text-[#6b6b6b] uppercase tracking-wider mb-3">
                {lang === "ar" ? "شارك مباشرة على" : "Share directly to"}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <a href={whatsappHref} target="_blank" rel="noopener noreferrer"
                   className="flex flex-col items-center gap-1 py-3 rounded-xl bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#128C7E] transition-colors"
                   data-testid="share-whatsapp">
                  <MessageCircle className="w-5 h-5" />
                  <span className="text-[11px] font-bold">WhatsApp</span>
                </a>
                <a href={twitterHref} target="_blank" rel="noopener noreferrer"
                   className="flex flex-col items-center gap-1 py-3 rounded-xl bg-[#0a0a0a]/5 hover:bg-[#0a0a0a]/10 text-[#0a0a0a] transition-colors"
                   data-testid="share-twitter">
                  <span className="text-lg font-black" style={{ fontFamily: "system-ui" }}>𝕏</span>
                  <span className="text-[11px] font-bold">Twitter</span>
                </a>
                <a href={telegramHref} target="_blank" rel="noopener noreferrer"
                   className="flex flex-col items-center gap-1 py-3 rounded-xl bg-[#0088cc]/10 hover:bg-[#0088cc]/20 text-[#0077b5] transition-colors"
                   data-testid="share-telegram">
                  <Send className="w-5 h-5" />
                  <span className="text-[11px] font-bold">Telegram</span>
                </a>
              </div>
            </div>

            {/* Tip card — clarifies the "live link" concept */}
            <div
              className="rounded-2xl p-4 text-xs text-[#5a4615] leading-relaxed"
              style={{ background: "linear-gradient(135deg,rgba(232,200,115,0.15),rgba(201,165,92,0.08))",
                       border: "1px solid rgba(201,165,92,0.35)" }}
            >
              <div className="font-bold text-[#3d2f0e] mb-1.5 inline-flex items-center gap-1.5">
                <Gift className="w-3.5 h-3.5" /> {lang === "ar" ? "معلومة مهمّة" : "Important"}
              </div>
              {lang === "ar"
                ? "الصورة تُظهر الرابط بشكل جميل. وعند الضغط على «مشاركة» أو أزرار واتساب/تويتر/تيليجرام أدناه، تُرسَل الصورة **مع نص يحوي رابطاً حيّاً قابلاً للنقر** — بضغطة واحدة يفتح كارتال."
                : "The card shows the link visually. Tapping Share (or the WhatsApp/Twitter/Telegram buttons) sends the image **together with a live clickable link** — one tap opens CARTAL."}
            </div>

            {/* Direct visit — visible page link so user can also try the platform */}
            <a
              href={SHARE_URL_BASE}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between rounded-2xl p-4 bg-[#0a0a0a] text-[#f1ecdf] hover:bg-[#1a1a1a] transition-colors group"
              data-testid="visit-platform"
            >
              <div>
                <div className="text-[10px] uppercase tracking-widest opacity-60">
                  {lang === "ar" ? "زُر المنصّة" : "Visit the platform"}
                </div>
                <div className="text-lg font-bold" dir="ltr">cartal.sa</div>
              </div>
              <ExternalLink className="w-5 h-5 text-[#c9a55c] group-hover:scale-110 transition-transform" />
            </a>
          </div>

          {/* ── Card preview (right) ───────────────────────────────────── */}
          <div className="order-1 lg:order-2">
            <div className="lg:sticky lg:top-24 flex justify-center">
              <GiftCard ref={cardRef} name={name} note={note} tagline={tagline} lang={lang} />
            </div>
          </div>
        </div>

        {/* Back to home */}
        <div className="text-center mt-10">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-[#8a6f2e] hover:text-[#5a4615]">
            <ArrowRight className="w-4 h-4 rtl:rotate-180" />
            {lang === "ar" ? "العودة إلى كارتال" : "Back to CARTAL"}
          </Link>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// GiftCard — the visual asset that gets exported to PNG (1080×1350 @ 2.5×)
// Portrait 4:5 — ideal for Instagram feed / X / WhatsApp story previews.
// ═══════════════════════════════════════════════════════════════════════════
const GiftCard = React.forwardRef(function GiftCard({ name, note, tagline, lang }, ref) {
  const displayName = name || (lang === "ar" ? "اسم المستلم" : "Recipient's name");
  const filled = Boolean(name);
  return (
    <div
      ref={ref}
      className="relative overflow-hidden shadow-2xl"
      style={{
        width: 480,          // renders large @ 2.5× ≈ 1200 wide
        height: 760,         // TALLER — plenty of room, nothing gets cut
        maxWidth: "94vw",
        maxHeight: "160vw",
        borderRadius: 32,
        color: "#f1ecdf",
        fontFamily: "'Cairo', 'Noto Sans Arabic', system-ui, sans-serif",
        background: `
          radial-gradient(60% 40% at 82% 8%, rgba(232,200,115,0.35) 0%, transparent 60%),
          radial-gradient(60% 40% at 8% 92%, rgba(138,111,46,0.35) 0%, transparent 60%),
          radial-gradient(90% 60% at 50% 50%, #1a140a 0%, #0a0806 60%, #050505 100%)
        `,
      }}
      data-testid="gift-card"
      dir={lang === "ar" ? "rtl" : "ltr"}
    >
      {/* Grain texture overlay for premium film feel */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          opacity: 0.08,
          pointerEvents: "none",
          background:
            "repeating-linear-gradient(45deg, rgba(255,255,255,0.05) 0px, rgba(255,255,255,0.05) 1px, transparent 1px, transparent 3px)",
          mixBlendMode: "overlay",
        }}
      />

      {/* Top golden accent line */}
      <div
        style={{
          position: "absolute",
          top: 0,
          insetInline: 0,
          height: 4,
          background:
            "linear-gradient(90deg,transparent 0%,#8a6f2e 15%,#f5d478 50%,#8a6f2e 85%,transparent 100%)",
        }}
      />
      {/* Bottom golden accent line */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          insetInline: 0,
          height: 4,
          background:
            "linear-gradient(90deg,transparent 0%,#8a6f2e 15%,#f5d478 50%,#8a6f2e 85%,transparent 100%)",
        }}
      />

      {/* Corner ornaments (Arabic-style corner brackets) */}
      {["top-left", "top-right", "bottom-left", "bottom-right"].map((corner) => (
        <div
          key={corner}
          aria-hidden
          style={{
            position: "absolute",
            width: 28,
            height: 28,
            borderColor: "#c9a55c",
            borderStyle: "solid",
            borderWidth: 0,
            opacity: 0.7,
            ...(corner === "top-left"     && { top: 16, left: 16,   borderTopWidth: 2, borderLeftWidth: 2,  borderTopLeftRadius: 6 }),
            ...(corner === "top-right"    && { top: 16, right: 16,  borderTopWidth: 2, borderRightWidth: 2, borderTopRightRadius: 6 }),
            ...(corner === "bottom-left"  && { bottom: 16, left: 16,  borderBottomWidth: 2, borderLeftWidth: 2,  borderBottomLeftRadius: 6 }),
            ...(corner === "bottom-right" && { bottom: 16, right: 16, borderBottomWidth: 2, borderRightWidth: 2, borderBottomRightRadius: 6 }),
          }}
        />
      ))}

      {/* Content stack */}
      <div style={{
        position: "relative",
        height: "100%",
        padding: "34px 30px 26px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}>
        {/* ── Header: logo + teaser badge ───────────────────────────── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img
              src={LOGO_URL}
              alt="CARTAL"
              crossOrigin="anonymous"
              style={{
                width: 46, height: 46, borderRadius: 12,
                boxShadow: "0 4px 14px rgba(201,165,92,0.35)",
              }}
            />
            <div style={{ lineHeight: 1 }}>
              <div
                style={{
                  fontSize: 20,
                  letterSpacing: "0.16em",
                  fontWeight: 900,
                  fontFamily: "'Cinzel', 'Playfair Display', serif",
                  background: "linear-gradient(135deg,#f5d478 0%,#c9a55c 50%,#8a6f2e 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                CARTAL
              </div>
              <div style={{ fontSize: 8, opacity: 0.55, letterSpacing: "0.20em", marginTop: 3 }}>
                DRIVE MORE · LIVE BETTER
              </div>
            </div>
          </div>
          {/* Teaser pill: "الإطلاق الرسمي" + launch date underneath */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 10px",
                borderRadius: 999,
                border: "1px solid rgba(201,165,92,0.5)",
                background: "rgba(232,200,115,0.10)",
                fontSize: 9,
                letterSpacing: "0.18em",
                color: "#f5d478",
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#f5d478", boxShadow: "0 0 6px #f5d478" }} />
              {lang === "ar" ? "الإطلاق الرسمي" : "OFFICIAL LAUNCH"}
            </div>
            {/* Launch date — visible directly under the pill */}
            <div
              style={{
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: "0.10em",
                color: "#f5d478",
                textShadow: "0 1px 6px rgba(232,200,115,0.4)",
              }}
              dir="ltr"
            >
              06 · 06 · 2026
            </div>
          </div>
        </div>

        {/* ── Middle: invitation ─────────────────────────────────────── */}
        <div style={{ textAlign: "center", padding: "0 4px" }}>
          {/* Ornament divider */}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            marginBottom: 12,
          }}>
            <span style={{ height: 1, flex: "0 0 40px", background: "linear-gradient(90deg,transparent,#c9a55c)" }} />
            <span style={{ color: "#c9a55c", fontSize: 12 }}>✦</span>
            <span style={{ height: 1, flex: "0 0 40px", background: "linear-gradient(90deg,#c9a55c,transparent)" }} />
          </div>

          <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 14, letterSpacing: "0.03em" }}>
            {lang === "ar" ? "دعوة خاصة إلى" : "A special invite for"}
          </div>

          {/* Name pill — the show-stopper */}
          <div
            style={{
              display: "block",
              margin: "0 auto",
              padding: filled ? "16px 28px" : "18px 28px",
              borderRadius: 20,
              border: `2px ${filled ? "solid" : "dashed"} #c9a55c`,
              maxWidth: 380,
              background: filled
                ? "linear-gradient(135deg,rgba(245,212,120,0.18) 0%,rgba(201,165,92,0.12) 50%,rgba(138,111,46,0.10) 100%)"
                : "rgba(255,255,255,0.02)",
              boxShadow: filled
                ? "0 8px 24px -4px rgba(232,200,115,0.28), inset 0 1px 0 rgba(245,212,120,0.20)"
                : "inset 0 1px 0 rgba(255,255,255,0.05)",
            }}
          >
            <div
              style={{
                fontSize: displayName.length > 18 ? 22
                        : displayName.length > 12 ? 26
                        : 32,
                fontWeight: 900,
                lineHeight: 1.2,
                color: filled ? "#f5d478" : "rgba(241,236,223,0.4)",
                fontFamily: "'Cairo', 'Noto Sans Arabic', system-ui, sans-serif",
                textShadow: filled ? "0 2px 12px rgba(232,200,115,0.4)" : "none",
                wordBreak: "break-word",
                overflowWrap: "anywhere",
              }}
            >
              {displayName}
            </div>
          </div>

          {/* Hero tagline */}
          <div style={{ marginTop: 22 }}>
            <div
              style={{
                fontSize: 20,
                fontWeight: 800,
                lineHeight: 1.35,
                color: "#f1ecdf",
                fontFamily: "'Cairo', 'Noto Sans Arabic', system-ui, sans-serif",
                padding: "0 8px",
              }}
            >
              {tagline.ar_hero}
            </div>
            <div style={{ fontSize: 11, opacity: 0.55, marginTop: 6, letterSpacing: "0.06em", fontStyle: "italic" }}>
              {tagline.en_hero}
            </div>
          </div>

          {/* Teaser copy — builds curiosity */}
          <div
            style={{
              marginTop: 14,
              fontSize: 12,
              lineHeight: 1.55,
              color: "rgba(241,236,223,0.75)",
              padding: "0 10px",
              fontWeight: 500,
            }}
          >
            {tagline.ar_tease}
          </div>

          {/* Optional personal note */}
          {note && (
            <div
              style={{
                marginTop: 14,
                padding: "10px 20px",
                fontSize: 12.5,
                fontStyle: "italic",
                color: "#f5d478",
                borderInlineStart: "3px solid #c9a55c",
                textAlign: "start",
                maxWidth: 420,        // widened from 320
                width: "92%",         // full-width flexible fit
                marginLeft: "auto",
                marginRight: "auto",
                borderRadius: 4,
                background: "rgba(232,200,115,0.06)",
                lineHeight: 1.55,
              }}
            >
              “{note}”
            </div>
          )}
        </div>

        {/* ── Footer: prominent direct URL to CARTAL homepage ────────── */}
        <div>
          {/* Ornament divider */}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            marginBottom: 14,
          }}>
            <span style={{ height: 1, flex: "0 0 40px", background: "linear-gradient(90deg,transparent,#c9a55c)" }} />
            <span style={{ color: "#c9a55c", fontSize: 12 }}>✦</span>
            <span style={{ height: 1, flex: "0 0 40px", background: "linear-gradient(90deg,#c9a55c,transparent)" }} />
          </div>

          <div style={{ textAlign: "center" }}>
            {/* Tiny label above the URL */}
            <div style={{
              fontSize: 10,
              opacity: 0.6,
              letterSpacing: "0.24em",
              marginBottom: 8,
              fontWeight: 700,
              color: "#f1ecdf",
            }}>
              {lang === "ar" ? "زُر المنصّة الرسميّة" : "VISIT THE PLATFORM"}
            </div>

            {/* Massive golden URL — the star of the footer.
                iter110: now a REAL <a> link (was a static <div>). Tapping
                it opens https://cartal.sa in a new tab. */}
            <a
              href={SHARE_URL_BASE}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="classic-cartal-live-cta"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                padding: "16px 40px",
                borderRadius: 999,
                background: "linear-gradient(135deg,#f5d478 0%,#e8c873 30%,#c9a55c 60%,#8a6f2e 100%)",
                color: "#0a0a0a",
                fontWeight: 900,
                fontSize: 30,
                letterSpacing: "0.03em",
                textDecoration: "none",
                boxShadow: "0 14px 34px rgba(232,200,115,0.55), inset 0 1.5px 0 rgba(255,255,255,0.45), inset 0 -1.5px 0 rgba(0,0,0,0.15)",
                border: "2px solid rgba(245,212,120,0.95)",
                cursor: "pointer",
              }}
              dir="ltr"
            >
              <span style={{ fontSize: 22 }}>✦</span>
              <span>cartal.sa</span>
              <span style={{ fontSize: 22 }}>✦</span>
            </a>

            {/* Sub-line building the promise */}
            <div style={{
              fontSize: 11,
              opacity: 0.65,
              letterSpacing: "0.14em",
              marginTop: 12,
              fontWeight: 600,
            }}>
              {lang === "ar"
                ? "المنصّة السعوديّة للسيارات"
                : "SAUDI'S AUTOMOTIVE SUPER-APP"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
